import { faShare, faEye, faX } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef } from "react";
import Tooltip from '@mui/material/Tooltip';
import { AppShareState } from "../../Map.types";
import { toast } from 'react-toastify';
import { WebSettingPreset } from "../../../../components/sitebase/Base.types";
import CollapsableBox from "../CollapsableBox";
import { StateToStringOptions } from "../../hooks/usePresets";

export default function PresetWidget(props: {
    onLoadPreset: (presetValue: string) => void,
    onSavePreset: (presets: WebSettingPreset) => void,
    onDeletePreset: (name: string) => void,
    shareState: AppShareState,
    presets: WebSettingPreset,
    stateToString: (state: AppShareState, options?: StateToStringOptions) => string
}) {
    const presetNameFieldRef = useRef<HTMLInputElement>(null);
    const forecastCheckedRef = useRef<HTMLInputElement>(null);
    const mapPositionCheckedRef = useRef<HTMLInputElement>(null);
    const layersCheckedRef = useRef<HTMLInputElement>(null);
    const layerSelectorViewCheckedRef = useRef<HTMLInputElement>(null);
    const baseMapCheckedRef = useRef<HTMLInputElement>(null);

    return <div className='d-flex flex-column'>
        <div role="button" aria-expanded="false" className="mx-auto" onClick={() => {
            const stateString = props.stateToString(props.shareState);
            window.navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}?${stateString}`);
            toast("Map URL copied to clipboard. You can paste it to share the current map view.")
        }}>
            <div className='btn btn-outline-dark'>
                <div className='d-flex flex-row'>
                    <div role='button' className='me-2'>Share current view</div>
                    <FontAwesomeIcon icon={faShare} />
                </div>
            </div>
        </div>
        <div className='mt-3'>
            <div className='fw-bold mb-2' style={{ borderBottom: "3px solid orange" }}>New Preset</div>
            <form className="form" onSubmit={(e) => {
                e.preventDefault();
                props.onSavePreset({
                    [presetNameFieldRef.current?.value ?? ""]: {
                        date: new Date().toISOString().split('T')[0],
                        value: props.stateToString(props.shareState, {
                            includeForecast: forecastCheckedRef.current?.checked ?? false,
                            includeMapPosition: mapPositionCheckedRef.current?.checked ?? false,
                            includeLayers: layersCheckedRef.current?.checked ?? false,
                            includeLayerSelectorView: layerSelectorViewCheckedRef.current?.checked ?? false,
                            includeBaseMap: baseMapCheckedRef.current?.checked ?? false,
                        })
                    }
                })
            }}>
                <div className="d-flex flex-column justify-content-center align-items-center mb-2">
                    <div className="input-group mb-1">
                        <label htmlFor="presetName" className="input-group-text fs-8">Name</label>
                        <input ref={presetNameFieldRef} type="text" maxLength={28} className="form-control fs-8 w-50" name="presetName" id="presetName" required />
                        <input id="preset-save" type="submit" role="button" className="form-control fs-8 w-25 btn btn-warning" value="Save" style={{ borderTopRightRadius: "5px", borderBottomRightRadius: "5px" }} />
                    </div>
                    <CollapsableBox title="Additional configuration" defaultCollapsed={true}>

                        <div>
                            <input ref={forecastCheckedRef} className="form-check-input me-2" type="checkbox" value="" id="includeForecastDate" defaultChecked={false} />
                            <label className="form-check-label fs-8" htmlFor="includeForecastDate" >
                                Include forecast date
                            </label>
                        </div>
                        <div>
                            <input ref={mapPositionCheckedRef} className="form-check-input me-2" type="checkbox" value="" id="includeMapPosition" defaultChecked={true} />
                            <label className="form-check-label fs-8" htmlFor="includeMapPosition" >
                                Include map position
                            </label>
                        </div>
                        <div>
                            <input ref={layersCheckedRef} className="form-check-input me-2" type="checkbox" value="" id="includeLayers" defaultChecked={true} />
                            <label className="form-check-label fs-8" htmlFor="includeLayers">
                                Include layers
                            </label>
                        </div>
                        <div>
                            <input ref={layerSelectorViewCheckedRef} className="form-check-input me-2" type="checkbox" value="" id="includeLayers" defaultChecked={true} />
                            <label className="form-check-label fs-8" htmlFor="includeLayers">
                                Include layer selector view
                            </label>
                        </div>
                        <div>
                            <input ref={baseMapCheckedRef} className="form-check-input me-2" type="checkbox" value="" id="includeBaseMap" defaultChecked={true} />
                            <label className="form-check-label fs-8" htmlFor="includeBaseMap">
                                Include base map
                            </label>
                        </div>

                        <div className="fs-8 mt-2">Checked - the value is included in the preset.<br /> Not checked - the value is ignored.</div>
                    </CollapsableBox>
                </div>
            </form>
        </div>
        <div className='mt-1'>
            <div className='fw-bold mb-2' style={{ borderBottom: "3px solid orange" }}>Saved Presets</div>
            {Object.entries(props.presets).map((preset, index) => <div key={index} className='d-flex flex-row efas-preset-row p-1 rounded fs-8'>
                <div className='my-auto me-3 overflow-x-auto' style={{ maxWidth: "15rem" }}>{preset[0]} - {preset[1].date}</div>
                {/* <CollapsableBox title={`${preset[0]} - ${preset[1].date}`} defaultCollapsed={true}>
                    <div className="fs-8">Value: {preset[1].value}</div>
                </CollapsableBox> */}
                <Tooltip title="Load" placement="bottom" arrow disableInteractive>
                    <div className="btn p-1 ms-auto" onClick={() => { props.onLoadPreset(preset[1].value); }}>
                        <FontAwesomeIcon icon={faEye} />
                    </div>
                </Tooltip>

                <Tooltip title="Delete" placement="bottom" arrow disableInteractive>
                    <div className="btn p-1"
                        onClick={() => props.onDeletePreset(preset[0])}>
                        <FontAwesomeIcon icon={faX} />
                    </div>
                </Tooltip>

                <Tooltip title="Share" placement="bottom" arrow disableInteractive>
                    <div className="btn p-1"
                        onClick={() => {
                            window.navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}?${preset[1].value}`);
                            toast("Map URL copied to clipboard. You can paste it to share the current map view.")
                        }}>
                        <FontAwesomeIcon icon={faShare} />
                    </div>
                </Tooltip>

                {/* <Tooltip text={preset[1].value.split("&").map((v) => <div>{v}</div>)} arrowOffset={50} maxWidth={450}>
                    <div className="btn p-1"
                    ><FontAwesomeIcon icon={faInfo} /></div>
                </Tooltip> */}
            </div>
            )}
        </div>
    </div>
}